import { FeedbackData } from '../../../context/FeedbackSnackbarContext';
import { BulkApproveResult, EsgRecordUpdateResponse } from '../../../types/syntiro_honey';

/**
 * Builds the reviewer-facing snackbar for a metric-confirming PATCH, from the
 * alias-loop outcome the backend returns (ADR-0001 A1/B2). The message makes the
 * LEARNING visible: confirming a mapping is not just fixing one record — it
 * teaches the per-client alias, so the same source label maps automatically on
 * every future upload. A CONFLICT is surfaced as a warning: the label has had
 * conflicting confirmations and will always need review.
 * @param {EsgRecordUpdateResponse} updated The PATCH response.
 * @returns {FeedbackData} The snackbar payload.
 */
export function aliasOutcomeFeedback(updated: EsgRecordUpdateResponse): FeedbackData {
  const label = updated.rawLabel;
  switch (updated.aliasOutcome) {
    case 'CREATED':
      return {
        state: true,
        type: 'success',
        message: `Metric confirmed — "${label}" now maps automatically for this client.`
      };
    case 'ALREADY_ACTIVE':
      return {
        state: true,
        type: 'success',
        message: 'Metric confirmed.'
      };
    case 'CONFLICT':
      return {
        state: true,
        type: 'warning',
        message: `Metric set for this record, but "${label}" has received conflicting confirmations. Resolve it under Learned rules to switch automatic mapping back on.`
      };
    default:
      // Older backend or a record without a company: the edit succeeded, the
      // alias store just did not learn from it.
      return { state: true, type: 'success', message: 'Metric confirmed.' };
  }
}

/**
 * Builds the learning summary for a BULK approval (ADR-0002: approving a suggested
 * mapping confirms it; ADR-0003: the response reports what that taught per record).
 * One toast for the whole batch, mirroring the bulk-confirm summary — or null when
 * the batch taught nothing (nothing to announce; the approvals themselves are
 * already summarised by the bulk-outcome alert).
 * @param {BulkApproveResult} outcome The bulk-approve response.
 * @returns {FeedbackData | null} The snackbar payload, or null when silent.
 */
export function bulkApprovalLearningFeedback(outcome: BulkApproveResult): FeedbackData | null {
  const outcomes = outcome.aliasOutcomes ?? [];
  const taught = outcomes.filter((entry) => entry.outcome === 'CREATED').length;
  const conflicts = outcomes.filter((entry) => entry.outcome === 'CONFLICT').length;
  if (taught === 0 && conflicts === 0) {
    return null;
  }
  const taughtPart = `${taught} label${taught === 1 ? '' : 's'} taught for this client`;
  if (conflicts > 0) {
    return {
      state: true,
      type: 'warning',
      message: `Approving also confirmed mappings — ${taughtPart}; ${conflicts} label${
        conflicts === 1 ? ' is' : 's are'
      } contested — resolve ${conflicts === 1 ? 'it' : 'them'} under Learned rules.`
    };
  }
  return {
    state: true,
    type: 'success',
    message: `Approving also confirmed mappings — ${taughtPart}; ${
      taught === 1 ? 'it' : 'they'
    } will now map automatically on future uploads.`
  };
}
